import React, { useCallback, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import { Controller, useFormContext } from 'react-hook-form-legacy';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.core.css';
import { FormGroup } from './FormGroup';
import InputError from './InputError';
import InputLabel from './InputLabel';
import ScrollToError from './ScrollToError';
import { getError } from './utils';
import { InfoHint } from '../hint/InfoHint';

type Props = {
  name: string;
  required?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  onChange?: (value: string) => void;
};

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    ['link'],
    ['clean'],
  ],
};

const EMPTY_CONTENT = '<p><br></p>';

export const RichtextInputStyled: React.FC<
  React.PropsWithChildren<ReactQuill.ReactQuillProps>
> = (props) => {
  return <ReactQuill {...props} modules={modules} />;
};

export const RichtextInput: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  label,
  required,
  disabled,
  hint,
  onChange,
}) => {
  const ref = useRef(null);
  const {
    control,
    errors,
    formState: { isSubmitting },
  } = useFormContext();

  const error = getError(name, errors);

  const handleChange = useCallback(
    (args: string[]) => {
      const [value] = args;
      const res = value === EMPTY_CONTENT ? '' : value;
      if (onChange) {
        onChange(res);
      }
      return res;
    },
    [onChange],
  );

  return (
    <FormGroup ref={ref}>
      {label && (
        <InputLabel
          label={label}
          required={required}
          hint={
            hint ? <InfoHint className="mt-2" text={String(hint)} /> : undefined
          }
        />
      )}
      <Controller
        name={name}
        control={control}
        as={
          <RichtextInputStyled
            className={[error ? 'is-invalid' : ''].join(' ')}
            readOnly={isSubmitting || disabled}
          />
        }
        onChange={handleChange}
      />
      <InputError error={error} />

      <ScrollToError name={name} inputContainerRef={ref} />
    </FormGroup>
  );
};
